import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('shirosec_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('shirosec_token');
            localStorage.removeItem('shirosec_user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth APIs
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    me: () => api.get('/auth/me'),
};

// Account APIs
export const accountAPI = {
    clearDemoData: () => api.delete('/account/clear-demo-data'),
};

// Dashboard APIs
export const dashboardAPI = {
    getStats: () => api.get('/dashboard/stats'),
};

// Devices APIs
export const devicesAPI = {
    getAll: () => api.get('/devices'),
    create: (data) => api.post('/devices', data),
    delete: (id) => api.delete(`/devices/${id}`),
};

// Threats APIs
export const threatsAPI = {
    getAll: (params) => api.get('/threats', { params }),
    get: (id) => api.get(`/threats/${id}`),
    resolve: (id) => api.post(`/threats/${id}/resolve`),
};

// Audit Logs APIs
export const auditAPI = {
    getLogs: (limit = 50) => api.get('/audit-logs', { params: { limit } }),
};

// Chat API
export const chatAPI = {
    send: (message, context = null) => api.post('/chat', { message, context }),
};

// Voice API
export const voiceAPI = {
    speak: (text) => api.post('/voice/speak', { text }),
    threatAlert: (threatId) => api.post(`/voice/threat-alert?threat_id=${threatId}`),
};

// Security Analysis API
export const securityAPI = {
    analyze: (event) => api.post('/security/analyze', event),
};

// Profile APIs
export const profileAPI = {
    get: () => api.get('/profile'),
    update: (data) => api.put('/profile', data),
    changePassword: (data) => api.post('/profile/change-password', data),
    deleteAccount: () => api.delete('/profile'),
};

// Subscription APIs
export const subscriptionAPI = {
    get: () => api.get('/subscription'),
    getPlans: () => api.get('/subscription/plans'),
    getUsage: () => api.get('/subscription/usage'),
    upgrade: (data) => api.post('/subscription/upgrade', data),
    cancel: () => api.post('/subscription/cancel'),
    getInvoices: () => api.get('/subscription/invoices'),
};

// Payment APIs
export const paymentsAPI = {
    getPlans: () => api.get('/payments/plans'),
    createCheckout: (planId, deviceCount = 1) => api.post('/payments/checkout', {
        plan_id: planId,
        origin_url: window.location.origin,
        device_count: deviceCount
    }),
    getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
    getHistory: () => api.get('/payments/history'),
};

// Agents APIs
export const agentsAPI = {
    list: () => api.get('/agent/list'),
    sendCommand: (agentId, commandType, params) => 
        api.post(`/agent/command?agent_id=${agentId}&command_type=${commandType}`, params),
    getDownloadKey: () => api.get('/agent/download-key'),
    // Scan APIs
    startScan: (agentId, scanType = 'quick') => 
        api.post(`/agent/scan/start?agent_id=${agentId}&scan_type=${scanType}`),
    getScanStatus: (scanId) => api.get(`/agent/scan/status/${scanId}`),
    getLatestScan: (agentId) => api.get(`/agent/scan/latest/${agentId}`),
    getScanHistory: (agentId, limit = 10) => api.get(`/agent/scan/history/${agentId}?limit=${limit}`),
};

// Scheduled Scans APIs
export const schedulesAPI = {
    create: (agentId, name, frequency, scanType = 'quick', hour = 2, dayOfWeek = null, dayOfMonth = null) =>
        api.post(`/schedules?agent_id=${agentId}&name=${encodeURIComponent(name)}&frequency=${frequency}&scan_type=${scanType}&hour=${hour}${dayOfWeek !== null ? `&day_of_week=${dayOfWeek}` : ''}${dayOfMonth !== null ? `&day_of_month=${dayOfMonth}` : ''}`),
    list: (agentId = null) => api.get(`/schedules${agentId ? `?agent_id=${agentId}` : ''}`),
    get: (scheduleId) => api.get(`/schedules/${scheduleId}`),
    update: (scheduleId, data) => api.put(`/schedules/${scheduleId}`, data),
    delete: (scheduleId) => api.delete(`/schedules/${scheduleId}`),
};

// MITRE ATT&CK APIs
export const mitreAPI = {
    getStats: () => api.get('/mitre/stats'),
    listTactics: () => api.get('/mitre/tactics'),
    listTechniques: (tactic = null, includeSubTechniques = false) => 
        api.get(`/mitre/techniques${tactic || includeSubTechniques ? '?' : ''}${tactic ? `tactic=${tactic}` : ''}${tactic && includeSubTechniques ? '&' : ''}${includeSubTechniques ? 'include_sub_techniques=true' : ''}`),
    getTechnique: (techniqueId) => api.get(`/mitre/techniques/${techniqueId}`),
    getTechniquesByCategory: (category) => api.get(`/mitre/techniques/by-category/${category}`),
    buildAttackChain: (techniqueIds) => api.post('/mitre/attack-chain', techniqueIds),
    mapFindings: (findings) => api.post('/mitre/map-findings', findings),
};

// Threat Hunting Playbooks APIs
export const playbooksAPI = {
    list: (category = null, severity = null) => 
        api.get(`/playbooks${category || severity ? '?' : ''}${category ? `category=${category}` : ''}${category && severity ? '&' : ''}${severity ? `severity=${severity}` : ''}`),
    getCategories: () => api.get('/playbooks/categories'),
    getByTechnique: (techniqueId) => api.get(`/playbooks/by-technique/${techniqueId}`),
    get: (playbookId) => api.get(`/playbooks/${playbookId}`),
};

// Security Intelligence Summary API
export const intelligenceAPI = {
    getSummary: () => api.get('/intelligence/summary'),
};

// AI Threat Analysis APIs
export const analysisAPI = {
    analyzeThreat: (threatId) => api.post(`/analyze/threat?threat_id=${threatId}`),
    analyzeScan: (scanId) => api.post(`/analyze/scan?scan_id=${scanId}`),
    analyzeFindings: (findings) => api.post('/analyze/findings', findings),
    listAnalyses: (limit = 20) => api.get(`/analyses?limit=${limit}`),
    createThreatsFromScan: (scanId, minSeverity = 'high') => 
        api.post(`/threats/from-scan/${scanId}?min_severity=${minSeverity}`),
};

// Enterprise API Key Management (for Settings page - uses JWT auth)
export const enterpriseAPI = {
    createApiKey: (name, permissions = ['read']) => 
        api.post(`/enterprise/api-keys/create?name=${encodeURIComponent(name)}&${permissions.map(p => `permissions=${p}`).join('&')}`),
    listApiKeys: () => api.get('/enterprise/api-keys/list'),
    revokeApiKey: (keyId) => api.delete(`/enterprise/api-keys/revoke/${keyId}`),
    // Webhooks (JWT auth versions)
    createWebhook: (name, url, events) => 
        api.post(`/enterprise/webhooks/create?name=${encodeURIComponent(name)}&url=${encodeURIComponent(url)}&${events.map(e => `events=${e}`).join('&')}`),
    listWebhooks: () => api.get('/enterprise/webhooks/list'),
    deleteWebhook: (webhookId) => api.delete(`/enterprise/webhooks/remove/${webhookId}`),
};

export default api;
